/* eslint-disable react-hooks/exhaustive-deps */
import React from "react"
import {useTranslation} from "react-i18next"
import {formatCurrency, formatDateOnly, getFullName, handleError} from "helpers"
import {KlassappTableHOC} from "HOC"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableProps} from "types/common"
import {FinAidRefund, RefundStatus} from "types/fin-aid/refund"
import {BaseButton} from "components"
import Status from "../Status"
import styles from "./RefundTable.module.css"
import {routing} from "helpers/routing"
import {useHistory, useParams} from "react-router-dom"
import {finAidRefundsService} from "services"
import {useModel} from "hooks"

interface Props extends KlassappTableProps {}

const RefundTable: React.FC<Props> = ({
    page,
    total,
    pageSize,
    columns,
    data,
    allFields,
    fields,
    orderField,
    tableHeaderActions,
    isShowTableHeaderAction,
    isLoading,
    dispatch,
    dispatchFunc,
    onChangeFields,
    onDraggableColumn,
    onChangeAllFields,
    onUpdateRowData,
    onUpdateTableData,
    onChangePage,
    onChangeRowPerPage,
    getCurrentData
}) => {
    const {t} = useTranslation(["financialAid", "common"])
    const history = useHistory()
    const model = useModel()
    const params = useParams<{studentId: string}>()
    const studentId = +params.studentId
    const userDateFormat = model.getUserDateFormat()

    const getRefundList = React.useCallback(async () => {
        dispatch({isLoading: true})
        try {
            const {data: rows, total: totalCount} = await finAidRefundsService.list({
                filter: {
                    studentProfileId: studentId || undefined
                },
                range: {
                    page,
                    pageSize
                },
                sort: orderField?.field ? {orderBy: orderField.field, orderDir: orderField.order} : undefined
            })
            const mapped = rows.map((row) => ({
                ...row,
                id: row.refundId,
                name: row.description || row.studentName || `Refund #${row.refundId}`,
                type: row.refundType
            }))
            dispatch({data: mapped, total: totalCount})
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }, [page, pageSize, orderField, studentId])

    const handleRowClick = React.useCallback(
        (row: FinAidRefund & {id: number}) => {
            history.push(routing.finAid.student.refundDetail(studentId || row.studentProfileId, row.refundId))
        },
        [history, studentId]
    )

    const tableOptions = React.useMemo(
        () => [
            {key: "getPageTitle", func: () => t("refund.table.title")},
            {key: "getListData", func: getRefundList},
            {
                key: "getFields",
                func: () => [
                    t("refund.table.columns.studentId"),
                    t("refund.table.columns.studentName"),
                    t("refund.table.columns.refundId"),
                    t("refund.table.columns.name"),
                    t("refund.table.columns.type"),
                    t("refund.table.columns.amount"),
                    t("refund.table.columns.updatedAt"),
                    t("refund.table.columns.status"),
                    t("refund.table.columns.action")
                ]
            },
            {key: "onClickRowItem", func: handleRowClick},
            {key: "getTableHeaderActions", func: () => []}
        ],
        [t, getRefundList, handleRowClick]
    )

    const getColumns = React.useCallback(
        () => [
            {
                title: t("refund.table.columns.studentId"),
                field: "customProfileId",
                sortable: true
            },
            {
                title: t("refund.table.columns.studentName"),
                field: "firstName",
                sortable: true,
                render: (_value, record: FinAidRefund) => getFullName(record)
            },
            {
                title: t("refund.table.columns.refundId"),
                field: "refundId",
                sortable: true
            },
            {
                title: t("refund.table.columns.name"),
                field: "description",
                sortable: true,
                render: (value, record: FinAidRefund) => value || `Refund #${record.refundId}`
            },
            {
                title: t("refund.table.columns.term"),
                field: "termName",
                orderField: "termId",
                sortable: true
            },
            {
                title: t("refund.table.columns.type"),
                field: "refundType",
                sortable: true
            },
            {
                title: t("refund.table.columns.amount"),
                field: "amount",
                sortable: true,
                render: (value) => (value != null ? formatCurrency(value) : "")
            },
            {
                title: t("refund.table.columns.updatedAt"),
                field: "updatedAt",
                sortable: true,
                render: (value) => (value ? formatDateOnly(value, userDateFormat) : "")
            },
            {
                title: t("refund.table.columns.status"),
                field: "status",
                sortable: true,
                render: (value: RefundStatus) => <Status status={value} />
            },
            {
                title: t("refund.table.columns.action"),
                field: "action",
                render: (_value, record: FinAidRefund) => (
                    <BaseButton
                        title={t("common:action.view")}
                        variant="secondary"
                        className={styles.viewBtn}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleRowClick({...record, id: record.refundId})
                        }}
                    />
                )
            }
        ],
        [t, userDateFormat, handleRowClick]
    )

    React.useEffect(() => {
        dispatchFunc(tableOptions)
        dispatch({columns: getColumns(), allFields: getColumns(), fields: getColumns().map((col) => col.title)})
    }, [tableOptions, getColumns])

    React.useEffect(() => {
        getRefundList()
    }, [getRefundList])

    return (
        <div>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onClickRowItem={handleRowClick}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onChangeFields={onChangeFields}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
                menuActions={[]}
            />
        </div>
    )
}

export default KlassappTableHOC(RefundTable)
